import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { and, doc, getDocs, or, orderBy, query, setDoc, where } from 'firebase/firestore'
import {
    getCountForQuery,
    getOrdersCollection,
    getProjectCollection,
} from '../../../../services/firebase/firebaseConfig'
import { clearUserRoleCache } from '../../../../helpers/checkHasAccess'
import { chunk } from '../../../../helpers/chunk'

const initialState = {
    id: null,
    email: null,    
    firstName: null,
    lastName: null,
    phone: null,
    isDriver: false,
    isSBCustomer: false,
    isVendor: false,
    isCarrier: false,
    carrier: null,

    customerProfileImageLink: null,
    needToCheckDispatch: null,    
    completedGuidesWalkthrough: false,
    systemSetupHasShown: false,
    isDarkTheme: false,
        
    isAllDistributionZones: false,
    userDistributionZoneIds: [],
    distributionzones: [],
    distributionzonesForReverse: [],
    stores: [],
    storesMap: {},
    stemsList: [],
    stemsMap: {},    
    manufacturers: [],
    suppliers: [],    
    transitLanes: [],
    
    regions: [],
    regionsMap: {},
        
    isAllClients: false,
    userClientIds: [],
    clients: [],
    
    actions: {},
    role: {},

    // Per-mode pinned default views: { [mode]: viewId | null }. Sourced from the
    // login validation payload so the shipments page doesn't re-read the USERS doc.
    defaultViews: {},

    userDataLoaded: false,
}

const isSuperAdmin = (roleName: string) => {
    // TBD: translation of role name and actions
    return roleName === 'SUPER_ADMIN'
}

const fetchAuthorizedDistributionZones = async (
    roleTitle,
    userDistributionZoneIds,
    isAllDistributionZones
): Promise<any> => {
    if (!userDistributionZoneIds && !isAllDistributionZones) return Promise.resolve([])

    const chunkSize = 10

    const queryArray: any = []

    // TODO: translation
    if (isSuperAdmin(roleTitle) || isAllDistributionZones) {
        queryArray.push(
            getDocs(
                query(
                    getProjectCollection(`${process.env.REACT_APP_COLLECTION_DISTRIBUTIONZONES}`),
                    orderBy('DistributionZoneName', 'asc')
                )
            )
        )
    } else {
        const distributionZoneList = [...userDistributionZoneIds]

        while (distributionZoneList.length > 0) {
            const chunk = distributionZoneList.splice(0, chunkSize)
            queryArray.push(
                getDocs(
                    query(
                        getProjectCollection(`${process.env.REACT_APP_COLLECTION_DISTRIBUTIONZONES}`),
                        where('DistributionZoneId', 'in', chunk),
                        orderBy('DistributionZoneName', 'asc')
                    )
                )
            )
        }
    }
    try {
        const results = await Promise.all(queryArray)
        return results.flatMap((q: any) => [...q.docs])
    } catch (error) {
        console.error('Error fetching zones:', error)
        return []
    }
}

const fetchAuthorizedClients = async (roleTitle, userClientIds, isAllClients): Promise<any> => {
    if (!userClientIds && !isAllClients) return Promise.resolve([])

    const chunkSize = 10

    const queryArray: any = []

    if (isSuperAdmin(roleTitle) || isAllClients) {
        queryArray.push(getDocs(query(getProjectCollection(`${process.env.REACT_APP_COLLECTION_CLIENTS}`))))
    } else {
        const clientIdList = [...userClientIds]

        while (clientIdList.length > 0) {
            const chunk = clientIdList.splice(0, chunkSize)
            queryArray.push(
                getDocs(
                    query(
                        getProjectCollection(`${process.env.REACT_APP_COLLECTION_CLIENTS}`),
                        where('id', 'in', chunk),
                        where('active', '==', true)
                    )
                )
            )
        }
    }
    try {
        const results = await Promise.all(queryArray)
        return results.flatMap((q: any) => [...q.docs])
    } catch (error) {
        console.error('Error fetching zones:', error)
        return []
    }
}

const countNeedToCheckDispatch = async (distributionzones, clients, roleName): Promise<any> => {
    // If there are no zones or clients, return 0 early
    if (!distributionzones.length || !clients.length) return 0;
    
    const distributionZoneNames = distributionzones.map((d: any) => d.data().DistributionZoneName);
    const clientNames = clients.map((c: any) => c.data().businessUnit + '***' + c.data().subBusinessUnit);
    // Process in batches to avoid hitting Firestore query limits
    const batchSize = 10;
    let totalCount = 0;
    
    const chunkedDistributionZoneNames = chunk(distributionZoneNames, batchSize);

    await Promise.all(chunkedDistributionZoneNames.map(async (zoneBatch) => {
        // Create a single query for each batch of zones
        const q = query(
            getProjectCollection('TMS_ORDERS'),
            and(
                where('fields.DistributionZone', 'in', zoneBatch),
                or(
                    where('fields.CustomerRequestInfo.OrderStatus', '==', 'Customer Requesting Reschedule'),
                    where('fields.OrderStatus', '==', 'Pending (Customer Portal)')
                )
            )
        );
        
        // Get the count for this batch
        const docs = await getDocs(q);

        const clientFilteredDocs = docs.docs.filter((doc: any) => {
            const client = doc.data().fields.Vendor.BusinessUnit + '***' + doc.data().fields.Vendor.SubBusinessUnit;
            return clientNames.includes(client);
        });

        totalCount += clientFilteredDocs.length;
        return Promise.resolve();
    }));
    
    return totalCount;
}

export const updateGuideWalkthrough: any = createAsyncThunk(
    'user/updateGuideWalkthrough',
    async (arg, { getState }) => {
        try {
            /* updating user with completedGuidesWalkthrough state if user skips it or completes the guide */
            const state: any = getState()
            const { user } = state
            /* user is in USERS collection , we update the firestore document  */
            const userDoc = doc(getProjectCollection(`${process.env.REACT_APP_COLLECTION_USERS}`), user.id)
            return await setDoc(userDoc, { completedGuidesWalkthrough: true }, { merge: true })
        } catch (error) {
            console.error('Error updating guide walkthrough:', error)
            return Promise.reject(error)
        }
    }
)

export const updateSystemSetupStatus: any = createAsyncThunk(
    'user/updateSystemSetupStatus',
    async (arg, { getState }) => {
        try {
            /* updating user with systemSetupHasShown state when user closes or completes the system setup modal */
            const state: any = getState()
            const { user } = state
            /* user is in USERS collection , we update the firestore document  */
            const userDoc = doc(getProjectCollection(`${process.env.REACT_APP_COLLECTION_USERS}`), user.id)
            return await setDoc(userDoc, { systemSetupHasShown: true }, { merge: true })
        } catch (error) {
            console.error('Error updating system setup status:', error)
            return Promise.reject(error)
        }
    }
)

export const fetchUserData: any = createAsyncThunk('user/fetchUserData', async (arg, { getState }) => {
    try {
        const state: any = getState()
        const { user } = state
        const [distributionzones, regionDocs, clients, transitLanes, customerProfile] = await Promise.all([
            /* returning only dist zones that user is authorized to have access to */
            fetchAuthorizedDistributionZones(user.role.name, user.userDistributionZoneIds, user.isAllDistributionZones),
            getOrdersCollection('REGIONS').get(),
            fetchAuthorizedClients(user.role.name, user.userClientIds, user.isAllClients),
            getOrdersCollection('LANES').get(),
            getOrdersCollection('CONFIGURATIONS').doc('PROFILE').get(),
        ])
        const countNeedToCheck = await countNeedToCheckDispatch(distributionzones, clients, user.role.name)
        
        const distributionZones: any[] = []
        let stemsMap = {}
        const regions = new Set()
        const reverseZones: any[] = []
        const storeList: any[] = []
        const stemsList: any[] = []
        const suppliers: any[] = []
        const manufacturers: any[] = []

        let storesMap = {}

        const distributionZoneIds = distributionzones.map((d: any) => d.id)

        distributionzones
            ?.sort((a, b) => (a.data().DistributionZoneName || '').localeCompare(b.data().DistributionZoneName || ''))
            .forEach((d: any) => {
                if (d.data()?.DistributionZoneType === 2) {
                    stemsMap = {
                        ...stemsMap,
                        [d.data().Region]: stemsMap[d.data().Region]
                            ? [...stemsMap[d.data().Region], { label: d.data().DistributionZoneName, value: d.id }]
                            : [{ label: d.data().DistributionZoneName, value: d.id }],
                    }
                    stemsList.push({
                        label: `STEM-${d.data().DistributionZoneName}`,
                        value: { id: d.id, Region: d.data().Region },
                    })
                } else if (d.data()?.DistributionZoneType === 3) {
                    storeList.push({
                        label: `${d.data().DistributionZoneName}`,
                        value: { id: d.id, Region: d.data().Region, Client: d.data().Vendor, ...d.data(), 
                            Center: {
                                _lat: d.data()?.Center?._lat,
                                _long: d.data()?.Center?._long,
                            }, //Firebase GeoPoint is not serializable
                         },
                    })

                    storesMap = {
                        ...storesMap,
                        [d.id]: d.data().DistributionZoneName,
                    }
                } else if (d.data()?.DistributionZoneType === 0 || d.data()?.DistributionZoneType === 1) {
                    const element = {
                        label: d.data().DistributionZoneName,
                        value: {
                            id: d.id,
                            ...d.data(),
                            Center: {
                                _lat: d.data()?.Center?._lat,
                                _long: d.data()?.Center?._long,
                            }, //Firebase GeoPoint is not serializable
                        },
                    }

                    distributionZones.push(element)

                    if (d.data()?.IsForReverse) {
                        reverseZones.push(element)
                    }
                } else if (d.data()?.DistributionZoneType === 5) {
                    manufacturers.push({
                        label: d.data().DistributionZoneName,
                        value: { id: d.id, ...d.data() },
                    })
                } else if (d.data()?.DistributionZoneType === 4) {
                    suppliers.push({
                        label: d.data().DistributionZoneName,
                        value: { id: d.id, ...d.data() },
                    })
                }
                regions.add(d.data().Region)
            })

        const regionsArray: any[] = []
        let regionsMap = {}

        ;(regionDocs?.docs || []).forEach((r: any) => {
            if (regions.has(r.id)) {
                regionsArray.push({
                    value: { id: r.id, ...r.data() },
                    label: r.data().name,
                })

                regionsMap = {
                    ...regionsMap,
                    [r.id]: r.data().name.toUpperCase(),
                }
            }
        })

        const clientIds = clients.map((d: any) => d.id)

        const clientsArray = clients
            ?.map((d: any) => ({
                label: `${d.data().businessUnit}***${d.data().subBusinessUnit}`,
                value: { id: d.id, ...d.data() },
            }))
            .sort((a, b) => a.label.localeCompare(b.label))

        /* transitLanes */
        const transitLaneArray = transitLanes?.docs.map((d: any) => ({
            label: d.data().name,
            value: { id: d.id, ...d.data() },
        }))

        return {
            clients: clientsArray,
            stemsMap: stemsMap,
            stores: storeList,
            distributionzones: distributionZones,
            regions: regionsArray,
            regionsMap: regionsMap,
            needToCheckDispatch: countNeedToCheck,
            reverseZones: reverseZones,
            stemsList: stemsList,
            storesMap: storesMap,
            clientIds: clientIds,
            distributionZoneIds: distributionZoneIds,
            transitLanes: transitLaneArray,
            manufacturers: manufacturers,
            suppliers: suppliers,
            customerProfileImageLink: customerProfile?.data()?.LogoImageFile?.[0]?.url || '',
        }
    } catch (error) {
        console.error('Error fetching user data:', error)
        return Promise.reject(error)
    }
})

const userSlice = createSlice({
    name: 'user',
    initialState: initialState,
    reducers: {
        toggleUserTheme: (state) => {
            state.isDarkTheme = !state.isDarkTheme
        },
        updateRole: (state, action: any) => {
            state.role = action.payload
        },
        updateActions: (state, action: any) => {
            state.actions = action.payload
        },
        updateUserDistributionZoneIds: (state, action: any) => {
            state.userDistributionZoneIds = action.payload
        },
        initUserData: (state, action) => {
            state.role = action.payload.role
            state.isAllDistributionZones = action.payload.isAllDistributionZones
            state.userDistributionZoneIds = action.payload.userDistributionZoneIds
            state.isAllClients = action.payload.isAllClients
            state.isDarkTheme = action.payload.isDarkTheme
            state.userClientIds = action.payload.userClientIds
            state.email = action.payload.email
            state.firstName = action.payload.firstName
            state.lastName = action.payload.lastName
            state.phone = action.payload.phone
            state.isDriver = action.payload.isDriver
            state.isSBCustomer = action.payload.isSBCustomer
            state.isVendor = action.payload.isVendor
            state.isCarrier = action.payload.isCarrier || false
            state.carrier = action.payload.carrier || null
            state.id = action.payload.id
            state.completedGuidesWalkthrough = action.payload.completedGuidesWalkthrough
            state.systemSetupHasShown = action.payload.systemSetupHasShown
            state.defaultViews = action.payload.defaultViews || {}
        },
        // Update one mode's pinned default view in place (pin/unpin from the view
        // panel) so redux stays in sync with the USERS-doc write without a re-fetch.
        setDefaultView: (state, action) => {
            const { mode, viewId } = action.payload || {}
            if (!mode) return
            state.defaultViews = { ...(state.defaultViews || {}), [mode]: viewId }
        },
        resetUserData: (state) => {
            state.id = null
            state.role = ''
            state.userDistributionZoneIds = []
            state.userClientIds = []
            state.email = null
            state.firstName = null
            state.lastName = null
            state.phone = null
            state.isDriver = false
            state.isSBCustomer = false
            state.isVendor = false
            state.isCarrier = false
            state.carrier = null
            state.stores = []
            state.distributionzones = []
            state.distributionzonesForReverse = []
            state.manufacturers = []
            state.suppliers = []
            state.clients = []
            state.regions = []
            state.regionsMap = {}
            state.actions = {}
            state.stemsMap = {}
            state.needToCheckDispatch = null
            state.userDataLoaded = false
            state.userDataLoaded = false
            state.completedGuidesWalkthrough = false
            state.systemSetupHasShown = false
            state.defaultViews = {}
            state.storesMap = {}
            state.stemsList = []
            state.isDarkTheme = false
            clearUserRoleCache();
        },
        setUserDataLoaded: (state, action) => {
            state.userDataLoaded = action.payload
        },
    },
    extraReducers: {
        [fetchUserData.pending]: (state) => {
            state.userDataLoaded = false
        },
        [updateGuideWalkthrough.fulfilled]: (state) => {
            state.completedGuidesWalkthrough = true
        },
        [updateSystemSetupStatus.fulfilled]: (state) => {
            state.systemSetupHasShown = true
        },
        [fetchUserData.fulfilled]: (state, { payload }) => {
            state.stemsMap = payload.stemsMap
            state.distributionzones = payload.distributionzones
            state.distributionzonesForReverse = payload.reverseZones
            state.clients = payload.clients
            state.regions = payload.regions
            state.regionsMap = payload.regionsMap
            state.needToCheckDispatch = payload.needToCheckDispatch
            state.userDataLoaded = true
            state.stores = payload.stores
            state.storesMap = payload.storesMap
            state.stemsList = payload.stemsList
            state.userClientIds = payload.clientIds
            state.userDistributionZoneIds = payload.distributionZoneIds
            state.transitLanes = payload.transitLanes
            state.manufacturers = payload.manufacturers
            state.suppliers = payload.suppliers
            state.customerProfileImageLink = payload.customerProfileImageLink
        },
        [fetchUserData.rejected]: (state) => {
            state.userDataLoaded = false
        },
    },
})

export const selectHasAccessFunction = (state, callerMethod) => {
    const { actions, role } = state
    const roleName = role.name

    if (isSuperAdmin(roleName)) return true

    if (!actions) return false

    switch (callerMethod) {
        case 'handleTriggerNotification':
            return actions['RETRIGGER NOTIFICATIONS']
        case 'accessSearch':
            return actions['ACCESS SEARCH']
        case 'accessOrderHistory':
            return actions['ACCESS HISTORY']
        case 'handleOrderBillingState':
            return actions['HANDLE ORDER BILLING STATE']
        case 'handleOrderSettlementState':
            return actions['HANDLE ORDER SETTLEMENT STATE']
        case 'handleBothState':
        case 'handlerBillingStateBoth':
            return actions['HANDLE ORDER BILLING STATE'] && actions['HANDLE ORDER SETTLEMENT STATE']
        case 'unlockOrder':
            return actions['UNCONFIRM & UNLOCK ORDER']
        case 'lockOrderAccounting':
            return actions['LOCK ORDER - ACCOUNTING']
        case 'unlockVehicle':
            return actions['UNLOCK OPTIMIZED VEHICLES']
        case 'editOrderFromOrderDisplay':
            return actions['EDIT ORDER DETAILS']

        case 'handleToggleDraftStateForExports':
            return actions['TOGGLE DRAFT STATE FOR EXPORTS']
        case 'generateInvoices':
            return actions['GENERATE INVOICES']
        case 'generateSettlements':
            return actions['GENERATE SETTLEMENTS']
        case 'exportOrders':
            return actions['GENERATE INVOICES'] || actions['GENERATE SETTLEMENTS']

        case 'createCommingeldOrder':
            return actions['CREATE ORDER']
        case 'manageOrderTemplates':
            return actions['CREATE ORDER']
        case 'createDedicatedOrder':
            return actions['CREATE DEDICATED ORDER']
        case 'manageInventory':
            return actions['CREATE INVENTORY ITEM']
        case 'manageReverseLogistics':
            return actions['CREATE REVERSE LOGISTICS ORDER']
        case 'calculateStemMinPay':
            return actions['CALCULATE STEM MIN PAY']
        case 'managePending':
            return actions['ACCESS PENDINGS (CUSTOMER PORTAL)']
        case 'fetchFSC':
            return actions['FETCH FSC']

        case 'manageSharedView':
            return actions['UPDATE SHARED VIEW']
        case 'sharePublicView':
            return actions['SHARE PUBLIC VIEW']

        //context menu(right click action) && quickAction
        case 'Prints':
            return actions['PRINT ORDERS']
        case 'Create BOL':
            return actions['ACCESS BOL']
        case 'pushToDriver':
            return actions['PUSH TO DRIVER']
        case 'Reschedule Order':
            return actions['RESCHEDULE ORDERS']
        case 'Move to Unassigned':
            return actions['MOVE TO UNASSIGNED']
        case 'Send Text & Robocall - All Clients':
            return actions['SEND TEXT & ROBOCALL - ALL CLIENTS']
        case 'handleGeocoding':
            return actions['RE-GEOCODING']
        case 'Create Return To Store':
            return actions['CREATE RETURN TO STORE']
        case 'Create Store Stop':
            return actions['CREATE STORE STOP']
        case 'Cancel Selected Orders':
            return actions['CANCEL ORDERS']
        case 'Reactivate Orders':
            return actions['REACTIVATE ORDERS']
        case 'Send Rate Confirmation':
            return actions['SEND RATE CONFIRMATION']
        case 'Manage Carrier Rate':
            return actions['MANAGE CARRIER RATE']

        //orderEtailsExpandedView-OrderDisplay
        case 'Order Detail':
            return actions['VIEW ORDER DETAILS']
        case 'Product Detail':
            return actions['VIEW PRODUCT DETAILS']
        case 'Invoices':
            return actions['VIEW INVOICES']
        case 'Settlements':
            return actions['VIEW SETTLEMENTS']
        case 'Calculation Breakdown':
            return actions['VIEW CALCULATION BREAKDOWN']
        case 'Notification Logs':
            return actions['VIEW NOTIFICATION LOGS']

        //QuicActions
        case 'bulkUpdateField':
            return actions['BULK UPDATE FIELDS']

        default:
            return false
    }
}

export const selectHasAccessModule = (state, path) => {
    const { actions, role } = state

    const roleName = role.name

    if (isSuperAdmin(roleName)) return true

    if (!actions) return false

    switch (path) {
        case '/':
        case '/mainContainer':
        case '/dateRegion':
        case '/records':
        case '/orderTable':
        case '/search':
        case '/shipments':
            return true
        // return actions['ACCESS RECORDS'];
        case '/dashboard':
            return actions['ACCESS DASHBOARD']
        case '/dispatch':
            return actions['ACCESS DASHBOARD']
        case '/liveConnect':
            return actions['ACCESS RECORDS']
        case '/pendingShipments':
        case '/customerPortal':
            return actions['ACCESS PENDINGS (CUSTOMER PORTAL)']
        case '/reports':
        case '/analytics':
            return actions['ACCESS ANALYTICS']
        // Invoice dispute reviews — same audience that issues the invoices.
        case '/disputes':
            return actions['GENERATE INVOICES']
        case '/routes':
            return actions['ACCESS ROUTING']
        case '/assets':
            return actions['ACCESS ASSETS']
        case '/mapFleet':
            return actions['ACCESS NETWORK']
        case '/admin/vendor':
            return actions['ACCESS CLIENTS PROFILE']
        case '/admin/notifications':
            return actions['ACCESS NOTIFICATIONS']
        case '/admin/contractor':
            return actions['ACCESS CONTRACTORS PROFILE']
        case '/admin/reports':
            return actions['ACCESS REPORT']
        case '/admin/role':
            return actions['ACCESS ROLES']
        case '/admin/automations':
            return actions['ACCESS AUTOMATION']
        case '/admin/userRole':
            return actions['ACCESS USER MANAGEMENT']
        case '/admin/calendarSettings':
            return actions['ACCESS CALENDAR SETTINGS']
        case '/admin/fields':
            return actions['ACCESS FIELD SETTINGS']
        case '/admin/billing/substatus':
            return actions['ACCESS ORDER STATUSES']
        case '/admin/billing/ratetable':
            return actions['ACCESS RATE TABLES']
        case '/admin/billing/taxes':
            return actions['ACCESS TAXES']
        case '/admin/billing/fscrates':
        // case '/admin/billing/fsc':
            return actions['ACCESS FUEL SURCHARGE RATES']
        case '/admin/billing/weeklyfscrates':
            return actions['ACCESS WEEKLY FSC']
        case '/admin/billing/services':
            return actions['ACCESS SERVICES']
        case '/admin/billing/conditions':
            return actions['ACCESS CONDITIONS']
        case '/admin/services':
            return true // Service Types - allowed by default
        case '/admin/order-templates':
            return actions['CREATE ORDER']
        /*
         * Email intake and its templates: SUPER_ADMIN only (owner, 2026-08-26).
         *
         * Granted solely by the isSuperAdmin early return at the top of this function. Listed
         * here rather than left to `default: false` because the two are not the same thing —
         * a default is where paths land when nobody has decided about them, and these have been
         * decided. Anyone adding a role action for these later has to delete an explicit `false`
         * to do it, which is a deliberate act rather than an accident of the fallthrough.
         */
        case '/admin/email-templates':
        case '/email-intake':
            return false
        case '/admin/billing/tiersEscrow':
            return actions['ACCESS TIER AND ESCROW']
        case '/admin/integrations':
            return actions['ACCESS INTEGRATIONS']
        case '/admin/billing/globalUnits':
            return actions['ACCESS GLOBAL UNITS']
        case '/admin':
            return actions['ACCESS ADMIN']

        // /review is the customer invoice review page: authorisation is the token in
        // the URL, resolved server-side by disputePublicApi, so there is no portal
        // account to check here.
        case '/sharedview':
        case '/trackorder':
        case '/trackorder/:trackId':
        case '/review':
        case '/signup':
        case '/login':
        case '/forgotPassword':
        case '/changePassword':
        case '/help':
            return true //public routes
        default:
            return false
    }
}

export const {
    updateRole,
    updateUserDistributionZoneIds,
    initUserData,
    resetUserData,
    toggleUserTheme,
    updateActions,
    setUserDataLoaded,
    setDefaultView,
} = userSlice.actions

export default userSlice.reducer
